import { z } from 'zod';
import { SceneType } from '../enums';

/**
 * Выход AI Director (§16).
 *
 * Строгая схема — не украшение: модель обязана вернуть ровно это, иначе
 * ответ отбрасывается и запрос повторяется. Схема одна и та же для валидации
 * ответа и для structured output самого запроса.
 */

export const VoiceEmotionEnum = z.enum([
  'neutral',
  'confident',
  'warm',
  'excited',
  'serious',
  'calm',
  'questioning',
]);

/** §30: сценарий размечается по частям, а не целиком одной эмоцией. */
export const scriptSegmentSchema = z
  .object({
    text: z.string().trim().min(1).max(600),
    emotion: VoiceEmotionEnum.default('neutral'),
  })
  .strict();

/** §25: графика описывается словами, а не сразу рендерится. */
export const graphicsSpecSchema = z
  .object({
    kind: z.enum([
      'big_number',
      'chart',
      'list',
      'diagram',
      'lower_third',
      'text_block',
      'cta',
      'logo',
      'ui_mock',
    ]),
    title: z.string().trim().max(120).optional(),
    value: z.string().trim().max(60).optional(),
    items: z.array(z.string().trim().max(120)).max(6).optional(),
    description: z.string().trim().max(400).optional(),
  })
  .strict();

export const directorSceneSchema = z
  .object({
    type: z.enum(Object.values(SceneType) as [SceneType, ...SceneType[]]),
    durationSec: z.number().min(1).max(30),
    /** Реплика: пусто для сцен без речи. */
    script: z.array(scriptSegmentSchema).max(8).default([]),
    /** Что показать в кадре. Для сцен с речью — обстановка. */
    visualPrompt: z.string().trim().max(500).default(''),
    /**
     * Крупность плана (§19.2).
     *
     * Динамичная сцена оживляет кадр, и от того, какой кадр взят первым,
     * зависит всё: из лицевого крупного плана выходит говорящая голова,
     * что бы ни было написано в описании. Проверено сценой «бег по крыше» —
     * получилась комната.
     *
     * Решает режиссёр: он и придумывает кадр. По умолчанию средний план —
     * он подходит и разговору, и лёгкому движению.
     */
    framing: z.enum(['closeup', 'medium', 'full']).default('medium'),
    /**
     * Как сцена стыкуется с предыдущей (§19.2, §27).
     *
     * `continue` — то же место и то же действие продолжается: человек вышел
     * из кадра влево и входит справа, камера доводит движение. Такие сцены
     * снимаются от последнего кадра предыдущей, и переход между ними —
     * прямая склейка: плавное наложение поверх непрерывного действия
     * выглядит сбоем, а не приёмом.
     *
     * `cut` — новое место или новая мысль. По умолчанию, потому что
     * большинство роликов состоит из отдельных кадров.
     */
    continuity: z.enum(['cut', 'continue']).default('cut'),
    graphics: graphicsSpecSchema.optional(),
    /** Зачем эта сцена в ролике — попадает в лог для разбора. */
    reason: z.string().trim().max(200).default(''),
  })
  .strict();

export const directorOutputSchema = z
  .object({
    title: z.string().trim().min(1).max(80),
    /** Цель ролика своими словами: проверка, что задачу поняли (§16.1). */
    goal: z.string().trim().max(300).default(''),
    /**
     * Просил ли человек музыку прямо в задании (§2.4, §31).
     *
     * Музыка живёт в бренд-ките и по умолчанию выключена — это осознанное
     * решение: она нужна не каждому ролику. Но «сделай с музыкой» в описании
     * не должно упираться в галочку, которую ещё надо найти.
     */
    musicRequested: z.boolean().default(false),
    scenes: z.array(directorSceneSchema).min(1).max(30),
  })
  .strict();

export type DirectorOutput = z.infer<typeof directorOutputSchema>;
export type DirectorScene = z.infer<typeof directorSceneSchema>;
export type ScriptSegment = z.infer<typeof scriptSegmentSchema>;
export type GraphicsSpec = z.infer<typeof graphicsSpecSchema>;

/**
 * Что директор знает о возможностях пользователя.
 *
 * Контекст собирает сервис, а НЕ модель: она не должна догадываться,
 * есть ли аватар и какие образы существуют (agents/api-director-projects.md §2).
 */
export interface DirectorContext {
  prompt: string;
  aspectRatio: string;
  /** null = «Auto», директор сам решает по содержанию (§15). */
  targetDurationSec: number | null;
  language: string;
  captionsEnabled: boolean;
  persona: {
    hasAvatar: boolean;
    hasVoice: boolean;
    appearanceCount: number;
  };
  looks: Array<{ id: string; name: string }>;
  /** Максимальная длительность сцены у доступных провайдеров (§43). */
  maxSceneDurationSec: number;
  limits: {
    maxScenes: number;
    maxDurationSec: number;
  };
}
